import type { CarrierTask } from "@/api/carrier";
import { formatDateOnly, formatDateTime } from "@/lib/format";

type TaskSchedule = Pick<
  CarrierTask,
  "scheduledDate" | "scheduledWindow" | "readyAt" | "schedulingTimeZone"
>;

/** Task.scheduledDate is a calendar day encoded at UTC midnight, not an appointment. */
export function formatTaskScheduleDay(task: TaskSchedule): string {
  return formatDateOnly(task.scheduledWindow?.date ?? task.scheduledDate);
}

/** The booking already carries exact instants and its dispatch timezone. */
export function formatTaskPickupWindow(task: TaskSchedule): string | null {
  const window = task.scheduledWindow;
  if (!window) return null;
  const start = new Date(window.startAt);
  const end = new Date(window.endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: window.timeZone,
      timeZoneName: "short",
    });
    return `${formatter.format(start)} – ${formatter.format(end)}`;
  } catch {
    return `${window.startTime}–${window.endTime} (${window.timeZone})`;
  }
}

/** readyAt is a real instant; show it in the booking's dispatch timezone. */
export function formatTaskReadyAt(task: TaskSchedule): string {
  if (!task.readyAt) return "—";
  const date = new Date(task.readyAt);
  if (Number.isNaN(date.getTime())) return "—";
  if (task.schedulingTimeZone) {
    try {
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: task.schedulingTimeZone,
        timeZoneName: "short",
      }).format(date);
    } catch {
      // Older tasks can lack a usable dispatch timezone.
    }
  }
  return `${formatDateTime(task.readyAt)} (your time)`;
}
